import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, FormattedMessage } from 'react-intl';
import isEqual from 'lodash-es/isEqual';
import OkCancelModal from '@/shared/components/OkCancelModal';
import { CanCreate } from '@/shared/components/security';

import intlShape from '@/shared/utils/intlShape';
import ToastrService from '@/shared/services/ToastrService';
import moment from 'moment';
import classNames from 'classnames';
import DatePicker from '@/shared/components/input/DatePicker';
import Checkbox from '@/shared/components/input/Checkbox';
import FileUpload from '@/shared/components/input/FileUpload';
import InlineAttachment from '@/shared/components/InlineAttachment';
import api from '@/shared/api';
import config from '@/config';
import handleHttpError from '@/shared/services/HandleHttpError';
import TimeInput from '@/shared/components/input/TimeInput';
import { isBeforeMinimum, isEmptyHtmlField } from '@/shared/utils/validationHelpers';
import RichTextEditor from '@/shared/components/RichTextEditor';
import messages from './interactionMessages';
import InteractionTagMultiSelect from './InteractionTagMultiSelect';
import CanRead from '@/shared/components/security/CanRead';
import { LinkIcon } from '@/shared/components/Icons';
import FormattedListValue, { listItemMsg } from '@/shared/components/FormattedListValue';
import DataCacheService from '@/shared/services/DataCacheService';
import { FolderOpenIcon } from '@/shared/components/Icons';
import DocumentSelectorModal from '@/shared/components/DocumentSelectorModal';
import Button from '@/shared/components/Button';
import InteractionIcon from './InteractionIcon';
import LoadingAnimation from '@/shared/components/LoadingAnimation';
import AuthService from '@/shared/services/AuthService';

const mapInteraction = interaction => {
  const result = { ...interaction };
  if (!!result.date && typeof result.date === 'string') result.date = moment.utc(result.date);

  const utcDate = moment.utc(result.date);
  const localDate = utcDate.local();
  const minutes = moment.duration(localDate.format('HH:mm')).asMinutes();
  result.minutes = minutes;
  return result;
};

class EditInteractionModal extends Component {
  static propTypes = {
    onSave: PropTypes.func.isRequired,
    intl: intlShape.isRequired,
    onClose: PropTypes.func.isRequired,
    interactionId: PropTypes.string.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      interaction: null,
      saving: false,
      showValidation: false,
      initialDescription: null,
      documentUploadEndpoint: null,
      tags: [],
    };
  }
  componentDidMount = () => {
    const { interactionId } = this.props;

    const interactionPromise = api.base.get(
      `${config.appUrls.interactions}/api/interactions/${interactionId}`,
    );

    const tagsPromise = api.base.get(`${config.appUrls.interactions}/api/tags`);
    const documentsPromise = api.base.get(
      `${config.appUrls.documents}/api/documents/Links/${interactionId}/6`,
    );

    Promise.all([interactionPromise, tagsPromise, documentsPromise]).then(
      ([{ data: interaction }, { data: tags }, { data: attachments }]) => {
        const functionality =
          interaction.createdBy === AuthService.getUserId()
            ? 'InteractionsOwn'
            : 'InteractionsOthers';
        this.setState({
          interaction: mapInteraction(interaction),
          tags,
          initialDescription: interaction.description,
          attachments,
          documentUploadEndpoint: `${config.appUrls.documents}/api/documents?documentType=13&linkId=${interactionId}&documentLinkType=6&relationId=${interaction.relationId}`,
          readOnly: !AuthService.hasPermission({ functionality, privilege: 'update' }),
        });
      },
    );
  };

  titleChanged = value => {
    this.setState(state => ({
      interaction: {
        ...state.interaction,
        title: value,
      },
    }));
  };

  descriptionChanged = value => {
    this.setState(state => ({
      interaction: {
        ...state.interaction,
        description: value,
      },
    }));
  };

  saveInteraction = () => {
    const { onSave, intl } = this.props;
    const { interaction } = this.state;

    if (!interaction.date) {
      ToastrService.error(intl.formatMessage(messages.DateMandatory));
      return;
    }

    if (isEmptyHtmlField(interaction.title) && isEmptyHtmlField(interaction.description)) {
      ToastrService.error(intl.formatMessage(messages.TitleOrDescriptionMandatory));
      return;
    }

    const model = {
      ...interaction,
    };
    const time = moment.duration(model.minutes, 'minutes');
    const interactionDate = moment.utc(model.date);

    model.date = moment([
      interactionDate.year(),
      interactionDate.month(),
      interactionDate.date(),
      time.hours(),
      time.minutes(),
      0,
      0,
    ]);

    return api.base
      .put(`${config.appUrls.interactions}/api/interactions`, model)
      .then(({ data }) => {
        return onSave(data);
      }, handleHttpError(intl));
  };

  onDocumentUploaded = (error, attachment) => {
    const { intl } = this.props;
    if (error) {
      ToastrService.error(intl.formatMessage(messages.MsgEmailAttachmentUploadFailed));
      return;
    }
    this.setState(({ attachments }) => ({
      attachments: [...attachments, attachment],
    }));
  };

  deleteAttachment = attachment => {
    const { interactionId } = this.props;
    api.base
      .delete(`${config.appUrls.documents}/api/documents/links/${attachment.id}/${interactionId}/6`)
      .then(() => {
        this.setState(({ attachments }) => ({
          attachments: attachments.filter(x => x.id !== attachment.id),
        }));
      });
  };

  tagsChanged = tags => {
    this.setState(({ interaction }) => ({ interaction: { ...interaction, tags } }));
  };
  dateChanged = date => {
    this.setState(({ interaction }) => ({
      interaction: {
        ...interaction,
        date,
      },
    }));
  };
  linkDocumentsToInteraction = documents => {
    const { intl, interactionId } = this.props;
    return api.base
      .post(
        `${config.appUrls.documents}/api/documents/LinksMultiple`,
        documents.map(doc => ({
          documentId: doc.id,
          linkId: interactionId,
          linkType: 6,
        })),
      )
      .then(x => {
        this.setState(({ attachments }) => ({
          attachments: [...attachments, ...documents],
          showDocumentSelectorModal: false,
        }));
      })
      .catch(handleHttpError(intl));
  };

  showValidation = () => this.setState({ showValidation: true });
  isFormInvalid = () => {
    const { interaction } = this.state;
    if (!interaction) return true;
    if (isEmptyHtmlField(interaction.title) && isEmptyHtmlField(interaction.description))
      return true;
    if (!interaction.date || !moment(interaction.date).isValid()) return true;

    return false;
  };

  render() {
    const { intl, relationId, onClose } = this.props;
    const {
      interaction,
      dateFocused,
      saving,
      showValidation,
      initialDescription,
      showDocumentSelectorModal,
      attachments,
      documentUploadEndpoint,
      readOnly,
    } = this.state;

    const titleClassName = classNames('form-group', {
      'has-error':
        showValidation &&
        isEmptyHtmlField(interaction.title) &&
        isEmptyHtmlField(interaction.description),
    });
    const descriptionClassName = classNames('form-group', {
      'has-error':
        showValidation &&
        isEmptyHtmlField(interaction.title) &&
        isEmptyHtmlField(interaction.description),
    });
    const dateClassName = classNames('form-group', {
      'has-error': showValidation && !interaction.date,
    });

    return (
      <OkCancelModal
        size="lg"
        title={
          <span>
            <InteractionIcon type={interaction?.interactionType} intl={intl} />
            {interaction && (
              <span className="margin10L">
                <FormattedListValue list="interactiontype" id={interaction.interactionType} />
              </span>
            )}
          </span>
        }
        show
        onClose={onClose}
        onSave={this.saveInteraction}
        onHoverSave={this.showValidation}
        disabled={readOnly || saving || (showValidation && this.isFormInvalid())}
      >
        {interaction ? (
          <form ref={this.form}>
            <div className="form-horizontal form-labels-left container-fluid">
              <div className={dateClassName}>
                <label className="control-label col-sm-2">
                  {intl.formatMessage(messages.Date)}
                </label>
                <div className="col-sm-8">
                  <div style={{ display: 'flex' }}>
                    <DatePicker
                      placeholder={intl.formatMessage(messages.Date)}
                      date={interaction.date}
                      onDateChange={this.dateChanged}
                      focused={dateFocused}
                      onFocusChange={({ focused }) => this.setState({ dateFocused: focused })}
                      id="interactionDate"
                      required
                      block
                      small
                      disabled={readOnly}
                      isOutsideRange={isBeforeMinimum}
                    />
                    <div className="margin5L" style={{ width: '135px' }}>
                      <TimeInput
                        value={interaction.minutes}
                        id="interactionMinutes"
                        disabled={readOnly}
                        onChange={value =>
                          this.setState(state => ({
                            ...state,
                            interaction: {
                              ...state.interaction,
                              minutes: value,
                            },
                          }))
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className={titleClassName}>
                <label className="control-label col-sm-2">
                  {intl.formatMessage(messages.Title)}
                </label>
                <div className="col-sm-10">
                  <input
                    type="text"
                    autoFocus
                    value={interaction.title}
                    placeholder={intl.formatMessage(messages.Title)}
                    onChange={e => this.titleChanged(e.target.value)}
                    required
                    disabled={readOnly}
                    maxLength={500}
                    className="form-control"
                  />
                </div>
              </div>
              <div className={descriptionClassName}>
                <label className="control-label col-sm-2">
                  {intl.formatMessage(messages.Description)}
                </label>
                <div className="col-sm-10">
                  <RichTextEditor
                    onChange={newContent => this.descriptionChanged(newContent)}
                    base64Images
                    id="new"
                    initialValue={initialDescription}
                    readonly={readOnly}
                  />
                </div>
              </div>
              <CanCreate on="documents">
                <div className="form-group">
                  <label className="control-label col-sm-2">
                    {intl.formatMessage(messages.Attachments)}
                  </label>
                  <div className="col-sm-10">
                    {attachments && (
                      <div>
                        {attachments.map(attachment => (
                          <InlineAttachment
                            key={attachment.id}
                            attachment={attachment}
                            onDelete={readOnly ? undefined : this.deleteAttachment}
                          />
                        ))}
                      </div>
                    )}
                    {!readOnly && documentUploadEndpoint ? (
                      <div className="file-upload-wrapper">
                        <FileUpload
                          endpoint={documentUploadEndpoint}
                          onFileUploaded={(error, file) => this.onDocumentUploaded(error, file)}
                          removeSuccessfullAfter={5000}
                        />
                      </div>
                    ) : null}
                    {!readOnly ? (
                      <CanRead on="Documents">
                        <Button
                          btnClassName="btn-block margin10T"
                          onClick={() => this.setState({ showDocumentSelectorModal: true })}
                        >
                          <FolderOpenIcon className="margin5R" />
                          <FormattedMessage {...messages.ChooseDocumentInAdminPulse} />
                        </Button>
                      </CanRead>
                    ) : null}
                  </div>
                </div>
              </CanCreate>
              <div className="form-group">
                <label className="control-label col-sm-2">
                  {intl.formatMessage(messages.Tags)}
                </label>
                <div className="col-sm-10">
                  <InteractionTagMultiSelect
                    value={interaction.tags}
                    onChange={this.tagsChanged}
                    allowNew
                    idProp="label"
                    caseSensitive={false}
                    disabled={readOnly}
                  />
                </div>
              </div>
              {!readOnly && showDocumentSelectorModal && (
                <DocumentSelectorModal
                  show
                  initialRelation={interaction.relationId}
                  onDocumentsSelected={documents => this.linkDocumentsToInteraction(documents)}
                  onClose={() => this.setState({ showDocumentSelectorModal: false })}
                />
              )}
            </div>
          </form>
        ) : (
          <LoadingAnimation />
        )}
      </OkCancelModal>
    );
  }
}

export default injectIntl(EditInteractionModal);
